import type { Database } from '@/types/supabase'

type ProfileType = Database['public']['Enums']['profile_type']

export type HeaderMenuSection = 'nav' | 'cta' | 'drawer' | 'user'

export interface HeaderMenuItem {
  key: string
  label: string
  href?: string

  /** Desktop CTA 등에만 선택적으로 사용 */
  isPrimary?: boolean

  /** 조건부 노출 */
  showCondition?: () => boolean

  /** 드롭다운(자식) - Desktop/Mobile 공통 모델 */
  items?: HeaderMenuItem[]

  /**
   * Active 판정 루트 경로들
   * - 지정 안 하면 href 기반으로 자동 판정
   */
  activeMatchers?: string[]

  /** 런타임에서 계산됨 */
  isActive?: boolean
}

export interface HeaderMenuGroup {
  section: HeaderMenuSection
  title?: string
  items: HeaderMenuItem[]
}

/* ---------------------------------------------
 * helpers
 * --------------------------------------------- */

const normalizePath = (p: string) => {
  const trimmed = (p ?? '').trim()
  if (!trimmed) return '/'
  if (trimmed === '/') return '/'
  return trimmed.replace(/\/+$/, '')
}

const isPathActive = (pathname: string, matcher: string) => {
  const p = normalizePath(pathname)
  const m = normalizePath(matcher)

  if (m === '/') return p === '/'
  if (p === m) return true
  return p.startsWith(m + '/')
}

const applyVisibility = (item: HeaderMenuItem): boolean => {
  if (!item.showCondition) return true
  try {
    return item.showCondition()
  } catch {
    return false
  }
}

const computeItemActive = (item: HeaderMenuItem, pathname: string): HeaderMenuItem | null => {
  if (!applyVisibility(item)) return null

  const children = item.items
    ?.map((child) => computeItemActive(child, pathname))
    .filter(Boolean) as HeaderMenuItem[] | undefined

  const hasChildren = Boolean(children && children.length > 0)

  const matchers =
    item.activeMatchers && item.activeMatchers.length > 0
      ? item.activeMatchers
      : item.href
        ? [item.href]
        : []

  const selfActive = matchers.some((m) => isPathActive(pathname, m))
  const childActive = hasChildren ? (children as HeaderMenuItem[]).some((c) => c.isActive) : false

  return {
    ...item,
    ...(hasChildren ? { items: children } : {}),
    isActive: selfActive || childActive,
  }
}

export const computeGroupActive = (groups: HeaderMenuGroup[], pathname: string): HeaderMenuGroup[] => {
  const p = normalizePath(pathname)

  return groups
    .map((g) => {
      const items = g.items
        .map((it) => computeItemActive(it, p))
        .filter(Boolean) as HeaderMenuItem[]

      return { ...g, items }
    })
    .filter((g) => g.items.length > 0)
}

/* ---------------------------------------------
 * Guest (비로그인) - 퍼블릭 영역
 * - 요청 반영: 프로젝트 찾기 / 팀 찾기 / 프리랜서 찾기
 * --------------------------------------------- */

export const getGuestHeaderMenu = (): HeaderMenuGroup[] => {
  return [
    {
      section: 'nav',
      items: [
        { key: 'projects', label: '프로젝트 찾기', href: '/freelancer/projects' },
        { key: 'teams', label: '팀 찾기', href: '/freelancer/teams' },
        { key: 'freelancers', label: '프리랜서 찾기', href: '/freelancer/makers' },
      ],
    },
  ]
}

/* ---------------------------------------------
 * Public Client Menu (퍼블릭 영역 - /client)
 * - 비로그인: 프로젝트 등록, 견적 요청, 프리랜서로 전환
 * - 로그인: 프로젝트 등록, 견적 요청, 받은 견적, 채팅, 프리랜서로 전환
 * --------------------------------------------- */

export const getPublicClientMenu = (
  isLoggedIn: boolean,
  pathname: string
): HeaderMenuGroup[] => {
  const items: HeaderMenuItem[] = [
    {
      key: 'home',
      label: '홈',
      href: '/client/dashboard',
      activeMatchers: ['/client/dashboard'],
    },
    {
      key: 'project-register',
      label: '프로젝트 등록',
      href: '/client/projects/new',
      activeMatchers: ['/client/projects/new'],
    },
    {
      key: 'quotes-manage',
      label: '견적 관리',
      href: '/client/quotes',
      activeMatchers: ['/client/quotes', '/client/requests'],
    },
  ]

  if (isLoggedIn) {
    items.push(
      {
        key: 'chat',
        label: '채팅',
        href: '/client/chat',
        activeMatchers: ['/client/chat'],
      }
    )
  }

  items.push({
    key: 'inquiry',
    label: '문의',
    href: '/inquiries/new',
    activeMatchers: ['/inquiries/new'],
  })

  return computeGroupActive(
    [
      {
        section: 'nav',
        items,
      },
    ],
    pathname
  )
}

/* ---------------------------------------------
 * Public Freelancer Menu (퍼블릭 영역 - /, /freelancer)
 * - 비로그인: 프로젝트 찾기, 프리랜서 찾기, 팀 찾기, 고객으로 전환
 * - 로그인: 프로젝트 찾기, 동료 찾기, 관심 항목, 채팅, 고객으로 전환
 * --------------------------------------------- */

export const getPublicFreelancerMenu = (
  isLoggedIn: boolean,
  pathname: string
): HeaderMenuGroup[] => {
  const items: HeaderMenuItem[] = []

  if (isLoggedIn) {
    items.push(
      {
        key: 'home',
        label: '홈',
        href: '/freelancer/dashboard',
        activeMatchers: ['/freelancer/dashboard'],
      },
      {
        key: 'project',
        label: '프로젝트 찾기',
        href: '/freelancer/projects',
      },
      {
        key: 'peer',
        label: '동료 찾기',
        href: '/freelancer/peers',
        activeMatchers: ['/freelancer/peers'],
      },
      {
        key: 'mypage',
        label: '마이페이지',
        href: '/my/freelancer',
        activeMatchers: ['/my/freelancer'],
      },
      {
        key: 'chat',
        label: '채팅',
        href: '/freelancer/chat',
        activeMatchers: ['/freelancer/chat'],
      }
    )
  } else {
    items.push(
      { key: 'projects', label: '프로젝트 찾기', href: '/freelancer/projects' },
      { key: 'freelancers', label: '프리랜서 찾기', href: '/freelancer/makers' },
      { key: 'teams', label: '팀 찾기', href: '/freelancer/teams' }
    )
  }

  items.push({
    key: 'inquiry',
    label: '문의',
    href: '/inquiries/new',
    activeMatchers: ['/inquiries/new'],
  })

  // 고객용 링크 추가
  items.push({
    key: 'switch-to-client',
    label: '고객으로 전환',
    href: '/client',
  })

  return computeGroupActive(
    [
      {
        section: 'nav',
        items,
      },
    ],
    pathname
  )
}

/* ---------------------------------------------
 * Desktop (로그인 후)
 * - 요청 반영:
 *   - 프리랜서: 프로젝트 찾기 / 동료 찾기 / 관심 항목 / 채팅
 *   - 기업: 프로젝트 등록 / 견적 요청 / 받은 견적 / 채팅
 * --------------------------------------------- */

export const getDesktopHeaderMenu = (
  _mode: ProfileType | null,
  isCompany: boolean,
  isFreelancer: boolean,
  pathname: string
): HeaderMenuGroup[] => {
  const groups: HeaderMenuGroup[] = []

  // 기업(고객) 모드: 4개 고정 + 프리랜서로 전환
  if (isCompany) {
    groups.push({
      section: 'nav',
      items: [
        {
          key: 'home',
          label: '홈',
          href: '/client/dashboard',
          activeMatchers: ['/client/dashboard'],
        },
        {
          key: 'project-register',
          label: '프로젝트 등록',
          href: '/client/projects/new',
          activeMatchers: ['/client/projects/new'],
        },
        {
          key: 'quotes-manage',
          label: '견적 관리',
          href: '/client/quotes',
          activeMatchers: ['/client/quotes', '/client/requests'],
        },
        {
          key: 'chat',
          label: '채팅',
          href: '/client/chat',
          activeMatchers: ['/client/chat'],
        },
        {
          key: 'inquiry',
          label: '문의',
          href: '/inquiries/new',
          activeMatchers: ['/inquiries/new'],
        },
      ],
    })

    return computeGroupActive(groups, pathname)
  }

  // 프리랜서 모드: 4개 고정 + 고객으로 전환 (하위 경로 active까지 보장)
  if (isFreelancer) {
    groups.push({
      section: 'nav',
      items: [
        {
          key: 'home',
          label: '홈',
          href: '/freelancer/dashboard',
          activeMatchers: ['/freelancer/dashboard'],
        },
        {
          key: 'project',
          label: '프로젝트 찾기',
          href: '/freelancer/projects',
        },
        {
          key: 'peer',
          label: '동료 찾기',
          href: '/freelancer/peers',
          activeMatchers: ['/freelancer/peers'],
        },
        {
          key: 'interests',
          label: '관심 항목',
          href: '/my/freelancer/interests',
          activeMatchers: ['/my/freelancer/interests'],
        },
        {
          key: 'chat',
          label: '채팅',
          href: '/freelancer/chat',
          activeMatchers: ['/freelancer/chat'],
        },
        {
          key: 'inquiry',
          label: '문의',
          href: '/inquiries/new',
          activeMatchers: ['/inquiries/new'],
        },
      ],
    })

    return computeGroupActive(groups, pathname)
  }

  return []
}

/* ---------------------------------------------
 * Mobile (로그인 후)
 * - drawer 그룹들
 *
 * NOTE:
 * 현재 MobileMenu는 item.items(자식)을 렌더링하지 않아서,
 * 모바일에서 “동료 찾기(팀/프리랜서)”를 1개 항목 + 자식으로 만들면 접근 경로가 사라집니다.
 * 그래서 모바일은 당장은 flat 유지(팀/프리랜서 분리)하는 편이 안전합니다.
 * “모바일도 4개 완전 고정”을 원하면 MobileMenu 렌더를 children 지원하도록 바꿔야 합니다.
 * --------------------------------------------- */

export const getMobileHeaderMenu = (
  _mode: ProfileType | null,
  isCompany: boolean,
  isFreelancer: boolean,
  pathname: string
): HeaderMenuGroup[] => {
  const groups: HeaderMenuGroup[] = []

  // ✅ 경로 기반으로도 메뉴 결정 (프로필 모드 정보가 없어도 경로로 판단)
  const isClientRoute = (pathname || '').startsWith('/my/client')
  const isFreelancerRoute = (pathname || '').startsWith('/my/freelancer')

  // 기업 경로 또는 기업 모드
  if (isClientRoute || isCompany) {
    groups.push({
      section: 'drawer',
      title: '핵심',
      items: [
        { key: 'home', label: '홈', href: '/client/dashboard', activeMatchers: ['/client/dashboard'] },
        { key: 'project-register', label: '프로젝트 등록', href: '/client/projects/new', activeMatchers: ['/client/projects/new'] },
        { key: 'quotes-manage', label: '견적 관리', href: '/client/quotes', activeMatchers: ['/client/quotes', '/client/requests'] },
        { key: 'chat', label: '채팅', href: '/client/chat', activeMatchers: ['/client/chat'] },
        { key: 'inquiry', label: '문의', href: '/inquiries/new', activeMatchers: ['/inquiries/new'] },
      ],
    })

    // ✅ 모바일에서도 관리 메뉴 표시
    groups.push({
      section: 'drawer',
      title: '관리',
      items: [
        { key: 'profile', label: '내 프로필', href: '/my/client/settings', activeMatchers: ['/my/client/settings'] },
        { key: 'interests', label: '관심 항목', href: '/my/client/interests', activeMatchers: ['/my/client/interests'] },
        { key: 'settings', label: '설정', href: '/my/client/settings', activeMatchers: ['/my/client/settings'] },
        { key: 'billing', label: '결제/구독', href: '/my/client/billing', activeMatchers: ['/my/client/billing'] },
      ],
    })

    return computeGroupActive(groups, pathname)
  }

  // 프리랜서 경로 또는 프리랜서 모드
  if (isFreelancerRoute || isFreelancer) {
    groups.push({
      section: 'drawer',
      title: '핵심',
      items: [
        { key: 'home', label: '홈', href: '/freelancer/dashboard', activeMatchers: ['/freelancer/dashboard'] },
        { key: 'project-find', label: '프로젝트 찾기', href: '/freelancer/projects', activeMatchers: ['/freelancer/projects'] },
        // 모바일에서 동료 찾기 접근을 유지하려면 flat 2개가 안전
        { key: 'peers', label: '동료 찾기', href: '/freelancer/peers', activeMatchers: ['/freelancer/peers'] },

        // { key: 'teams', label: '팀 찾기', href: '/freelancer/teams', activeMatchers: ['/freelancer/teams'] },
        // { key: 'makers', label: '프리랜서 찾기', href: '/freelancer/makers', activeMatchers: ['/freelancer/makers'] },
        { key: 'interests', label: '관심 항목', href: '/my/freelancer/interests', activeMatchers: ['/my/freelancer/interests'] },
        { key: 'chat', label: '채팅', href: '/freelancer/chat', activeMatchers: ['/freelancer/chat'] },
        { key: 'inquiry', label: '문의', href: '/inquiries/new', activeMatchers: ['/inquiries/new'] },
      ],
    })

    // ✅ 모바일에서도 관리 메뉴 표시
    groups.push({
      section: 'drawer',
      title: '관리',
      items: [
        { key: 'profile', label: '내 프로필', href: '/my/freelancer/profile?tab=basic', activeMatchers: ['/my/freelancer/profile'] },
        { key: 'team', label: '내 팀', href: '/my/freelancer/teams', activeMatchers: ['/my/freelancer/teams', '/my/freelancer/team'] },
        { key: 'settings', label: '설정', href: '/my/freelancer/settings', activeMatchers: ['/my/freelancer/settings'] },
      ],
    })

    return computeGroupActive(groups, pathname)
  }

  return []
}

/* ---------------------------------------------
 * Mobile Public Client Menu (모바일 퍼블릭 영역 - /client)
 * - 비로그인: 프로젝트 등록, 견적 요청
 * - 로그인: 프로젝트 등록, 견적 요청, 받은 견적, 채팅
 * --------------------------------------------- */

export const getMobilePublicClientMenu = (
  isLoggedIn: boolean,
  pathname: string
): HeaderMenuGroup[] => {
  const items: HeaderMenuItem[] = [
    { key: 'home', label: '홈', href: '/client/dashboard', activeMatchers: ['/client/dashboard'] },
    { key: 'project-register', label: '프로젝트 등록', href: '/client/projects/new', activeMatchers: ['/client/projects/new'] },
    { key: 'quotes-manage', label: '견적 관리', href: '/client/quotes', activeMatchers: ['/client/quotes', '/client/requests'] },
  ]

  if (isLoggedIn) {
    items.push(
      { key: 'chat', label: '채팅', href: '/client/chat', activeMatchers: ['/client/chat'] }
    )
  }

  items.push({ key: 'inquiry', label: '문의', href: '/inquiries/new', activeMatchers: ['/inquiries/new'] })

  return computeGroupActive(
    [
      {
        section: 'drawer',
        title: '핵심',
        items,
      },
    ],
    pathname
  )
}

/* ---------------------------------------------
 * Mobile Public Freelancer Menu (모바일 퍼블릭 영역 - /, /freelancer)
 * - 비로그인: 프로젝트 찾기, 프리랜서 찾기, 팀 찾기
 * - 로그인: 프로젝트 찾기, 동료 찾기, 관심 항목, 채팅
 * --------------------------------------------- */

export const getMobilePublicFreelancerMenu = (
  isLoggedIn: boolean,
  pathname: string
): HeaderMenuGroup[] => {
  const items: HeaderMenuItem[] = []

  if (isLoggedIn) {
    items.push(
      { key: 'home', label: '홈', href: '/freelancer/dashboard', activeMatchers: ['/freelancer/dashboard'] },
      { key: 'project-find', label: '프로젝트 찾기', href: '/freelancer/projects', activeMatchers: ['/freelancer/projects'] },
      { key: 'peers', label: '동료 찾기', href: '/freelancer/peers', activeMatchers: ['/freelancer/peers'] },
      { key: 'interests', label: '관심 항목', href: '/my/freelancer/interests', activeMatchers: ['/my/freelancer/interests'] },
      { key: 'chat', label: '채팅', href: '/freelancer/chat', activeMatchers: ['/freelancer/chat'] }
    )
  } else {
    items.push(
      { key: 'projects', label: '프로젝트 찾기', href: '/freelancer/projects' },
      { key: 'freelancers', label: '프리랜서 찾기', href: '/freelancer/makers' },
      { key: 'teams', label: '팀 찾기', href: '/freelancer/teams' }
    )
  }

  items.push({ key: 'inquiry', label: '문의', href: '/inquiries/new', activeMatchers: ['/inquiries/new'] })

  return computeGroupActive(
    [
      {
        section: 'drawer',
        title: '핵심',
        items,
      },
    ],
    pathname
  )
}

/* ---------------------------------------------
 * User menu (마이페이지 드롭다운/시트)
 * --------------------------------------------- */

export const getUserMenuGroups = (isCompany: boolean, isFreelancer: boolean): HeaderMenuGroup[] => {
  if (isCompany) {
    return [
      {
        section: 'user',
        items: [
          { key: 'projects', label: '프로젝트 관리', href: '/my/client/projects' },
          { key: 'profile', label: '프로필 관리', href: '/my/client/settings' },
          { key: 'billing', label: '결제 관리', href: '/my/client/billing' },
          { key: 'bookmarks', label: '관심', href: '/my/client/interests' },
          { key: 'settings', label: '설정', href: '/my/client/settings' },
        ],
      },
    ]
  }

  if (isFreelancer) {
    return [
      {
        section: 'user',
        items: [
          { key: 'profile', label: '프로필 관리', href: '/my/freelancer/profile?tab=basic' },
          { key: 'team', label: '팀 관리', href: '/my/freelancer/teams' },
          { key: 'estimates', label: '견적 관리', href: '/my/freelancer/estimates' },
          { key: 'settings', label: '설정', href: '/my/freelancer/settings' },
        ],
      },
    ]
  }

  return []
}

/* ---------------------------------------------
 * 하위 호환(shim)
 * --------------------------------------------- */

export const getUserMenuItems = (isCompany: boolean, isFreelancer: boolean) => {
  const groups = getUserMenuGroups(isCompany, isFreelancer)
  return groups[0]?.items ?? []
}
